/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import Swal from "sweetalert2";
import userAccessTokenService from "../../services/user-access-token.service";
import { showApiKeyCopyDialog } from "../../utility/api-key-copy-dialog";
import Emitter from "../../utility/emitter";
import "./index.scss";

const ApiTokens = () => {
  const currentUser = useSelector((state) => state.currentUser?.user);
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const branchName = currentUser?.pharmacyBranchName;

  const loadTokens = useCallback(async () => {
    const result = await userAccessTokenService.List();
    if (result.error === null) {
      setTokens(result.data || []);
    }
  }, []);

  useEffect(() => {
    document.title = "TransferMyRx::API Tokens";
    const pageTitle = document.getElementById("page-title-text");
    if (pageTitle) {
      pageTitle.innerText = "API Tokens";
    }
    loadTokens();
  }, [loadTokens]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!name.trim() || isCreating) return;

    setIsCreating(true);
    const result = await userAccessTokenService.Create(name.trim());
    setIsCreating(false);

    if (result.error !== null || !result.data?.token) return;

    setName("");
    await showApiKeyCopyDialog({
      title: "Access token created",
      bodyHtml: `<p>Send this token to your partner. It is shown only once and cannot be recovered — if it is lost, revoke it and create a new one.</p>`,
      apiKey: result.data.token,
      prefix: result.data.tokenPrefix,
    });
    Emitter.emit("SHOW_NOTIFICATION", {
      message: "Access token created",
    });
    await loadTokens();
  };

  const handleRevoke = async (token) => {
    const confirm = await Swal.fire({
      title: "Revoke this token?",
      text: `Any partner using "${token.name}" will immediately lose access.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Revoke",
    });
    if (!confirm.isConfirmed) return;

    const result = await userAccessTokenService.Revoke(token.id);
    if (result.error === null) {
      await loadTokens();
    }
  };

  return (
    <div className="api-tokens">
      <div className="card h-100">
        <div className="card-header bg-white">
          <p className="mb-2">
            Access tokens let a partner system act on your behalf through the
            TransferMyRx Partner API — creating patients and sending or
            receiving transfers — without needing their own user account.
          </p>
          <p className="mb-0 text-muted small">
            A new token is bound to your current branch
            {branchName ? ` (${branchName})` : ""}. To create a token for
            another branch, switch branches first from the profile menu.
          </p>
        </div>

        <div className="card-body border-bottom">
          <form className="row g-2 align-items-end" onSubmit={handleCreate}>
            <div className="col-md-6">
              <label className="form-label" htmlFor="token-name">
                Token name
              </label>
              <input
                id="token-name"
                type="text"
                className="form-control"
                placeholder="e.g. SandsRx integration"
                maxLength={150}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="col-md-3">
              <button
                type="submit"
                className="btn btn-default"
                disabled={!name.trim() || isCreating}
              >
                <i className="fa fa-plus-circle me-1"></i>Generate token
              </button>
            </div>
          </form>
        </div>

        <div className="card-body datagrid p-0">
          <table className="table mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th className="text-center">Token</th>
                <th className="text-center">Created</th>
                <th className="text-center">Last used</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokens.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    You have not generated any access tokens yet.
                  </td>
                </tr>
              ) : (
                tokens.map((token) => (
                  <tr key={token.id}>
                    <td>{token.name}</td>
                    <td className="text-center token-prefix">
                      <code>{token.tokenPrefix}…</code>
                    </td>
                    <td className="text-center">
                      {token.createdOn
                        ? moment(token.createdOn).format("MM/DD/YY HH:mm")
                        : "—"}
                    </td>
                    <td className="text-center">
                      {token.lastUsedAt
                        ? moment(token.lastUsedAt).format("MM/DD/YY HH:mm")
                        : "Never"}
                    </td>
                    <td>
                      <div className="d-flex justify-content-center">
                        <a
                          href="#"
                          className="delete"
                          title="Revoke"
                          onClick={(event) => {
                            event.preventDefault();
                            handleRevoke(token);
                          }}
                        >
                          <i className="fa fa-ban"></i>
                        </a>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ApiTokens;
